/* eslint-disable max-lines */
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ContentHeadingData } from 'common/models/content-heading';
import { LanguageTranslationService } from 'common/services/language-translation.service';
import { EnrollmentOption, EnrollmentStatusPayload, ProgramDetails, ProgramEnrollment } from 'private/app/models/account-admin-program.model';
import { BaseResponse } from 'src/common/models/account-status';
import { AccountAdminApiService } from 'private/app/services/account-admin/account-admin-api.service';
import { AccountAdminProgramService } from 'private/app/services/account-admin/account-admin-program.service';
import { ToastService } from 'common/services/toast.service';
import { BehaviorSubject } from 'rxjs';
import { delay, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';
import { AccountAdminProgramEnrollmentService } from 'private/app/services/account-admin/account-admin-program-enrollment.service';
import { AppConstants } from 'common/app-constants';
import { OktaService } from 'common/services/okta/okta.service';
import { IdToken } from 'common/models';
import { IdmUserRoles } from 'private/app/enums/idm-user-roles';
import { BaseComponent } from 'common/components/base/base.component';
import { ManageEnrollmentsService } from 'private/app/services/manage-enrollments.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'hvac-manage-enrollments',
    templateUrl: './manage-enrollments.component.html',
    styleUrls: ['./manage-enrollments.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ManageEnrollmentsComponent extends BaseComponent implements OnInit {
    public breadCrumbData: ContentHeadingData = this.accountAdminProgramEnrollmentService.breadCrumbData;
    public manageEnrollmentsForm = this.accountAdminProgramEnrollmentService.manageEnrollmentsForm;
    public isLoading: boolean = true;
    public isTableLoading: boolean = false;
    public isMultipleEnroll: boolean = true;
    public disableExportButton: boolean = true;
    public isEnrollModalVisible: boolean = false;
    public isEnrollActionsEnabled: boolean = false;
    public programManagersList: string[] = [];
    public loggedInUserName: string;
    public isProgramManager?: boolean = false;
    public isDealerConversionProgram: boolean;
    public isEnrollmentLevel: boolean;
    public currentPage: number = AppConstants.currentPage;
    public pageSize: number = AppConstants.pageSize;
    public totalPages: number;
    public selectedTab: number = 0;
    public performAction: string;
    public enrollmentActionForm: FormGroup;
    public enrollmentAction: EnrollmentOption;
    public enrollment: ProgramEnrollment;
    public enrollmentConfirmMessage: string;
    public programData: ProgramDetails;
    public tabs: string[] = this.accountAdminProgramEnrollmentService.enrollmentStatusTabs;
    public currentPageEnrollmentData$ = new BehaviorSubject<ProgramEnrollment[]>([]);
    public filteredData$ = new BehaviorSubject<ProgramEnrollment[]>([]);
    public enrollmentStatus$ = new BehaviorSubject('');
    public showManageEnrollmentDropdowns$ = new BehaviorSubject<Boolean>(false);
    public isAllEnrllmntsSlctdFrCrntFltr = false;
    public hasAnySelection = false;
    public isFilterActive = false;
    public idmUserRole: string[];
    public loggedInUserRole: string;
    private searchFilteredData: ProgramEnrollment[] = [];
    private enrollmentStatusPayload: EnrollmentStatusPayload;
    private selectedEnrollments: Map<number, ProgramEnrollment> = new Map();

    constructor(
        public translate: TranslateService,
        public accountAdminProgramService: AccountAdminProgramService,
        public accountAdminProgramEnrollmentService : AccountAdminProgramEnrollmentService,
        private languageTranslationService: LanguageTranslationService,
        private route: ActivatedRoute,
        private router: Router,
        private readonly accountAdminApiService: AccountAdminApiService,
        private readonly toastService: ToastService,
        private oktaService: OktaService,
        private manageEnrollmentsService : ManageEnrollmentsService
    ) {
        super();
        this.languageTranslationService.translationsLoaded().pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            setTimeout(() => {
                this.accountAdminProgramService.setProgramAdminBreadCrumData(this.breadCrumbData);
            }, 0);
        });
    }

    ngOnInit(): void {
        this.accountAdminProgramEnrollmentService.resetManageEnrollmentsForm();
        if (this.route.snapshot.params.programId) {
            this.accountAdminApiService.getAccountAdminProgramsById(this.route.snapshot.params.programId).subscribe((data) => {
                this.programData = data;
                this.programManagersList = data.programManagers.map((manager) => `${(manager.firstName).toLowerCase()} ${(manager.lastName).toLowerCase()}`);
                this.isDealerConversionProgram = data.enrollmentWorkflow?.name === this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.MANAGE_ENROLLMENTS_PLACEHOLDER_TEXT.DEALER_CONVERSION_PROGRAM');
                if (!this.isDealerConversionProgram) {
                    this.tabs = this.accountAdminProgramEnrollmentService.nonDealerConversionEnrollmentStatusTabs;
                }
                this.isEnrollmentLevel = data.enrollmentLevel;
                this.showManageEnrollmentDropdowns$ = this.manageEnrollmentsService.checkDropdownVisibility(data);
                this.oktaService.idToken$.subscribe((res: IdToken) => {
                    this.idmUserRole = res?.claims?.idm_user_roles || [];
                    this.loggedInUserName = (res?.claims?.name) ? res?.claims?.name : '';
                    if (this.idmUserRole.includes(IdmUserRoles.DEFAULT_DISTRIBUTOR)) {
                        this.loggedInUserRole = IdmUserRoles.DEFAULT_DISTRIBUTOR;
                    }
                    if (this.idmUserRole.includes(IdmUserRoles.REGIONAL_SALES_MANAGER)) {
                        this.loggedInUserRole = IdmUserRoles.REGIONAL_SALES_MANAGER;
                    }
                    if (this.idmUserRole.includes(IdmUserRoles.DUCTLESS_RSM)) {
                        this.loggedInUserRole = IdmUserRoles.DUCTLESS_RSM;
                    }
                    if (this.idmUserRole.includes(IdmUserRoles.PROGRAM_MANAGER)) {
                        this.loggedInUserRole = IdmUserRoles.PROGRAM_MANAGER;
                    }
                    if (!this.programData.enrollmentWorkflow && (res?.claims?.idm_user_roles?.includes(IdmUserRoles.DEFAULT_INTERNAL) || res?.claims?.idm_user_roles?.includes(IdmUserRoles.DEFAULT_DISTRIBUTOR))) {
                        this.isEnrollActionsEnabled = true;
                    }
                    this.isProgramManager = (res?.claims?.idm_user_roles?.includes(IdmUserRoles.PROGRAM_MANAGER) && this.programManagersList.includes((this.loggedInUserName).toLowerCase()));
                });
                this.getAccountAdminProgramEnrollments();
            }, (error: HttpErrorResponse) => this.toastService.raiseDefaultErrorToast('ManageEnrollmentsComponent-ngOnInit', error.message));
        }
        this.enrollmentStatus$.pipe(takeUntil(this.ngOnDestroy$), distinctUntilChanged(), tap(() => this.isTableLoading = true), delay(AppConstants.enrollmentsSearchDelay)).subscribe(() => {
            this.filterProgramEnrollments();
        });
        this.manageEnrollmentsForm.valueChanges.pipe(takeUntil(this.ngOnDestroy$), distinctUntilChanged(), tap(() => this.isTableLoading = true), delay(AppConstants.enrollmentsSearchDelay)).subscribe(() => {
            this.filterProgramEnrollments();
        });
    }

    handleTabClick(tabSelection: number, tab: string) {
        this.isTableLoading = true;
        this.selectedTab = tabSelection;
        this.currentPage = AppConstants.currentPage;
        const val = this.accountAdminProgramEnrollmentService.tabValue(tab);
        this.enrollmentStatus$.next(val);
    }

    handlePageChange(page: number) {
        this.currentPage = parseInt(page.toString(), this.pageSize);
        const filterData = this.searchFilteredData.length ? this.searchFilteredData : this.filteredData$.value;
        this.currentPageEnrollmentData$.next(
            filterData.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize)
        );
    }

    onSelectDealerConversionEnrollment(enrollment: ProgramEnrollment, enrollmentAction: EnrollmentOption) {
        if (this.isDealerConversionProgram && (enrollmentAction.name === this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.DROPDOWN_OPTIONS.ENROLLMENT_ACTIONS.ACTIVE') || enrollmentAction.name === this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.DROPDOWN_OPTIONS.ENROLLMENT_ACTIONS.VIEW_ENROLLMENT'))) {
            this.accountAdminProgramEnrollmentService.openProgramEnrollmentForm(enrollment, this.programData);
        }
        else {
            this.enrollment = enrollment;
            this.enrollmentAction = enrollmentAction;
            if (this.enrollmentAction.name === 'Select') {
                this.enrollment = enrollment;
                this.enrollmentAction = enrollmentAction;

                return;
            }
            this.isEnrollModalVisible = true;
            this.enrollmentConfirmMessage = enrollmentAction.confirmDetails;
        }
    }

    showMultipleEnrollConfirmationModal(action: string) {
        this.isEnrollModalVisible = true;
        const messageMap: { [key: string]: string } = {
            'enroll': 'ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_ACTION_MESSAGE.SELECTED_ACTIVE',
            'deactivate': 'ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_ACTION_MESSAGE.SELECTED_INACTIVE',
            'reset eligible': 'ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_ACTION_MESSAGE.SELECTED_ELIGIBLE',
            'enrollAll': 'ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_ACTION_MESSAGE.ENROLL_ALL',
            'deactivateAll': 'ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_ACTION_MESSAGE.DEACTIVATE_ALL',
            'resetAll': 'ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_ACTION_MESSAGE.RESET_ALL'
        };
        this.enrollmentConfirmMessage = this.translate.instant(messageMap[action]);
        this.performAction = action;
    }

    handleCloseModal() {
        if (this.isEnrollModalVisible) {
            this.isEnrollModalVisible = false;
            this.enrollmentActionForm.controls[`enrollmentAction${this.enrollment.dealerId}`].setValue('', { emitEvent: true });
        }
    }

    handleOkModal() {
        this.isEnrollModalVisible = false;
        if (this.isMultipleEnroll && !this.performAction?.includes('All')) {
            this.confirmSingleEnrollment();
        }
        else if (this.performAction?.includes('All')) {
            this.confirmAllEnrollments();
        }
        else {
            this.confirmMultipleEnrollments();
        }
    }

    onCancel() {
        this.router.navigate(['/Admin/Program/search-program/search'],
            {
                relativeTo: this.route,
                queryParams: this.accountAdminProgramService.lastQueryParams.value,
                queryParamsHandling: 'merge'
            });
    }

    confirmSingleEnrollment() {
        if (this.enrollmentAction.value) {
            this.isTableLoading = true;
            if (this.enrollmentAction.enrollmentType === this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_STATUS.STATUS')) {
                this.enrollmentStatusPayload = {
                    programId: Number(this.programData.id),
                    dealerCompanies: [this.enrollment.dealerId],
                    enrollmentStatusId: Number(this.enrollmentAction.value)
                };
                this.accountAdminApiService.accountAdminProgramEnrollmentStatus(this.enrollmentStatusPayload).subscribe((data: BaseResponse) => {
                    if (data?.status === this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_STATUS.SUCCESS')) {
                        let message: string;
                        if (this.enrollment.enrollmentStatus === 0) {
                            const messageObject = {
                                dealerName: this.enrollment.dealerName,
                                dealerDisplayId: this.enrollment.dealerDisplayId,
                                programName: this.programData.name,
                                programCode: this.programData.code
                            };
                            message = this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_STATUS.ENROLLMENT_STATUS_SINGLE_SUCCESS', messageObject);
                        }
                        else {
                            message = data?.details;
                        }
                        this.displayToast(message, 'success');
                        this.getAccountAdminProgramEnrollments(true);
                    }
                },
                (error) => {
                    this.isTableLoading = false;
                    this.displayToast(error.error?.details, 'error');
                });
            }
            else if (this.enrollmentAction.enrollmentType === this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_STATUS.LEVEL')) {
                this.accountAdminApiService.accountAdminProgramEnrollmentLevel(Number(this.programData.id), this.enrollment.dealerId, Number(this.enrollmentAction.value)).subscribe((data: BaseResponse) => {
                    if (data?.status === this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_STATUS.SUCCESS')) {
                        this.displayToast(data?.details, 'success');
                        this.getAccountAdminProgramEnrollments(true);
                    }
                },
                (error) => {
                    this.isTableLoading = false;
                    this.displayToast(error.error?.message, 'error');
                });
            }
        }
    }

    confirmAllEnrollments() {
        this.isTableLoading = true;
        const statusMap: { [key: number]: { [key: string]: { enrollmentStatusId: number; currentStatusId: number } } } = {
            1: {
                enrollAll: {
                    enrollmentStatusId: 1,
                    currentStatusId: 0
                }
            },
            2: {
                resetAll: {
                    enrollmentStatusId: 0,
                    currentStatusId: 1
                },
                deactivateAll: {
                    enrollmentStatusId: 2,
                    currentStatusId: 1
                }
            },
            3: {
                resetAll: {
                    enrollmentStatusId: 0,
                    currentStatusId: 2
                },
                enrollAll: {
                    enrollmentStatusId: 1,
                    currentStatusId: 2
                }
            }
        };
        const config = statusMap[this.selectedTab]?.[this.performAction];
        if (config) {
            this.enrollmentStatusPayload = {
                programId: Number(this.programData.id),
                dealerCompanies: [0],
                ...config
            };
        }
        this.accountAdminApiService.accountAdminProgramEnrollmentStatusBulk(this.enrollmentStatusPayload).subscribe((data: BaseResponse) => {
            if (data?.status === this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_STATUS.SUCCESS')) {
                const messageObject = {
                    programName: this.programData.name,
                    programCode: this.programData.code
                };
                const message = this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_STATUS.ENROLLMENT_STATUS_MULTIPLE_SUCCESS', messageObject);
                this.displayToast(message, 'success');
                this.getAccountAdminProgramEnrollments(true);
            }
        },
        (error) => {
            this.isTableLoading = false;
            this.displayToast(error.error?.message, 'error');
        });
    }

    confirmMultipleEnrollments() {
        this.isTableLoading = true;
        const selectedItems = Array.from(this.selectedEnrollments.values()).map((dealer) => dealer.dealerId);
        const statusMap: { [key: number]: { [key: string]: { enrollmentStatusId: number; currentStatusId: number } } } = {
            1: {
                enroll: {
                    enrollmentStatusId: 1,
                    currentStatusId: 0
                }
            },
            2: {
                'reset eligible': {
                    enrollmentStatusId: 0,
                    currentStatusId: 1
                },
                'deactivate': {
                    enrollmentStatusId: 2,
                    currentStatusId: 1
                }
            },
            3: {
                'reset eligible': {
                    enrollmentStatusId: 0,
                    currentStatusId: 2
                },
                'enroll': {
                    enrollmentStatusId: 1,
                    currentStatusId: 2
                }
            }
        };
        const config = statusMap[this.selectedTab]?.[this.performAction];
        if (config) {
            this.enrollmentStatusPayload = {
                programId: Number(this.programData.id),
                dealerCompanies: selectedItems,
                ...config
            };
        }
        if (selectedItems.length < 5000) {
            this.accountAdminApiService.accountAdminProgramEnrollmentStatus(this.enrollmentStatusPayload).subscribe((data: BaseResponse) => {
                if (data?.status === this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_STATUS.SUCCESS')) {
                    const messageObject = {
                        programName: this.programData.name,
                        programCode: this.programData.code
                    };
                    const message = this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_STATUS.ENROLLMENT_STATUS_MULTIPLE_SUCCESS', messageObject);
                    this.displayToast(message, 'success');
                    this.getAccountAdminProgramEnrollments(true);
                }
            },
            (error) => {
                this.isTableLoading = false;
                this.displayToast(error.error?.message, 'error');
            });
        }
        else {
            this.isTableLoading = false;
            const exceedLimitMessage = this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.MANAGE_ENROLLMENTS_PLACEHOLDER_TEXT.ENROLL_EXCESS_LIMIT_MESSAGE');
            this.displayToast(exceedLimitMessage, 'error');
        }
    }

    selectEnrollment(enrollment: ProgramEnrollment) {
        enrollment.isChecked = !enrollment.isChecked;
        if (this.selectedEnrollments.get(enrollment.dealerId)) {
            this.selectedEnrollments.delete(enrollment.dealerId);
        }
        else {
            this.selectedEnrollments.set(enrollment.dealerId, enrollment);
        }
        this.hasAnySelection = this.selectedEnrollments.size > 0;
        this.isEnrollDisabled();
        const currentFilteredData = this.searchFilteredData.length ? this.searchFilteredData : this.filteredData$.value;
        this.isAllEnrllmntsSlctdFrCrntFltr = this.selectedEnrollments.size === currentFilteredData.length;
    }

    exportSelectedEnrollments() {
        this.accountAdminProgramEnrollmentService.exportSelectedEnrollments(this.programData.name, this.selectedEnrollments, this.isDealerConversionProgram, this.isEnrollmentLevel);
    }

    hasDeactivateEnrollmentOption(): boolean {
        const currentData = this.currentPageEnrollmentData$.value;

        return currentData?.some((enrollment) => enrollment.enrollmentOptions?.some((option) => option.name === 'Deactivate Enrollment')) || false;
    }

    hasEnrollDealerOption(): boolean {
        const currentData = this.currentPageEnrollmentData$.value;

        return currentData?.some((enrollment) => enrollment.enrollmentOptions?.some((option) => option.name === 'Enroll Dealer')) || false;
    }

    hasResetToEligibleOption(): boolean {
        const currentData = this.currentPageEnrollmentData$.value;

        return currentData?.some((enrollment) => enrollment.enrollmentOptions?.some((option) => option.name === 'Reset To Eligible')) || false;
    }

    handleSelectAll(event: Event) {
        this.accountAdminProgramEnrollmentService.handleSelectAllEnrollments(event, this.searchFilteredData.length ? this.searchFilteredData : this.filteredData$.value, this.selectedEnrollments);
        this.hasAnySelection = this.selectedEnrollments.size > 0;
        this.isEnrollDisabled();
        this.isAllEnrllmntsSlctdFrCrntFltr = true;
    }

    getAccountAdminProgramEnrollments(checkForFilter = false) {
        this.isTableLoading = true;
        const isEnrollmentFormEmpty = this.programData.enrollmentForm === null;
        const isEnrollmentWorkflowEmpty = this.programData.enrollmentWorkflow === null;
        this.accountAdminApiService.getAccountAdminProgramEnrollmentsById(this.programData.id).subscribe((enrollmentsData) => {
            this.isLoading = false;
            this.totalPages = Math.ceil(enrollmentsData.length / this.pageSize);
            this.filteredData$.next(enrollmentsData);
            const group: { [key: string]: FormControl } = {};
            enrollmentsData.forEach((item: ProgramEnrollment) => {
                item.isChecked = false;
                item.enrollmentOptions = this.accountAdminProgramEnrollmentService.getEnrollmentActionOptions(item, this.loggedInUserRole, isEnrollmentFormEmpty, isEnrollmentWorkflowEmpty);
                item.enrollmentOptions = (this.isProgramManager) ? item.enrollmentOptions : item.enrollmentOptions.filter((val) => val.value !== '0');
                group[`enrollmentAction${item.dealerId}`] = new FormControl('');
            });
            this.enrollmentActionForm = new FormGroup(group);
            if (checkForFilter) {
                this.filterProgramEnrollments();

                return;
            }
            this.currentPageEnrollmentData$.next(enrollmentsData.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize));
            this.isTableLoading = false;
        });
    }

    private isEnrollDisabled() {
        this.currentPageEnrollmentData$.subscribe((val: ProgramEnrollment[]) => {
            this.isMultipleEnroll = !val.some((item) => item.isChecked);
            this.disableExportButton = this.selectedEnrollments.size === 0;
        });
    }

    private displayToast(content: string, theme: 'success' | 'error') {
        this.accountAdminProgramService.smoothScrollToTop();
        this.toastService.raiseDefaultToastMessage(this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_TOAST_MESSAGE_ID'), content, theme);
    }

    private filterProgramEnrollments() {
        const formControls = this.manageEnrollmentsForm.value;
        this.isFilterActive = Boolean(formControls.dealerName || formControls.dealerDisplayId || formControls.city || formControls.state || formControls.workflowStatus || formControls.enrollmentStatus || formControls.sponsoringDistributor);
        this.searchFilteredData = this.filteredData$.value.map((enrollmentData) => {
            enrollmentData.isChecked = false;

            return enrollmentData;
        }).filter((item) => {
            let matchesWorkflowStatus = true;
            if (formControls.workflowStatus?.length) {
                const workflowStatusName = this.accountAdminProgramEnrollmentService.getWorkflowStatusName(item.enrollmentWorkFlowStatusId);
                matchesWorkflowStatus = workflowStatusName?.toLowerCase().includes(formControls.workflowStatus.toLowerCase());
            }
            let matchesEnrollmentStatus = true;
            if (formControls.enrollmentStatus?.length) {
                const enrollmentStatusName = this.accountAdminProgramEnrollmentService.getEnrollmentStatusName(item.enrollmentStatus);
                matchesEnrollmentStatus = enrollmentStatusName?.toLowerCase().includes(formControls.enrollmentStatus.toLowerCase());
            }

            return item.dealerName?.toLowerCase().includes(formControls.dealerName?.toLowerCase()) &&
                item.dealerDisplayId?.toString().toLowerCase().includes(formControls.dealerDisplayId?.toLowerCase()) &&
                item.city?.toLowerCase().includes(formControls.city?.toLowerCase()) &&
                item.state?.toLowerCase().includes(formControls.state?.toLowerCase()) &&
                matchesWorkflowStatus &&
                matchesEnrollmentStatus &&
                item.enrollmentStatus?.toString().toLowerCase().includes(this.enrollmentStatus$.value?.toLowerCase()) &&
                item.sponsoringDistributor?.toString().toLowerCase().includes(formControls.sponsoringDistributor?.toLowerCase());
        });
        if (this.isFilterActive && this.searchFilteredData.length > 5000 && this.selectedTab !== 0) {
            const exceedLimitMessage = this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.MANAGE_ENROLLMENTS_PLACEHOLDER_TEXT.ENROLL_EXCESS_LIMIT_MESSAGE');
            this.displayToast(exceedLimitMessage, 'error');
        }
        this.selectedEnrollments = new Map();
        this.hasAnySelection = false;
        this.isAllEnrllmntsSlctdFrCrntFltr = false;
        this.currentPage = AppConstants.currentPage;
        this.totalPages = Math.ceil(this.searchFilteredData.length / this.pageSize);
        this.currentPageEnrollmentData$.next(this.searchFilteredData.slice(0, this.pageSize));
        this.isTableLoading = false;
    }
}
